import { Link, useLocation } from 'react-router-dom';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const SupportPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en/');

  const TEXT_JA = {
    breadcrumb: { home: 'ホーム', troubleshooting: 'トラブル対策', title: 'サポート除去困難' },
    badge: '軽微',
    title: 'サポート除去困難',
    description: 'サポート材の除去が困難で、造形物を傷つけてしまう。設定の最適化で改善可能。',
    symptoms: {
      title: '症状の例',
      items: [
        'サポートが本体に強く接着して剥がせない',
        'サポート除去時に本体表面が傷つく',
        'サポート跡が目立つ',
        '細かいサポートが取り切れない'
      ]
    },
    causes: {
      title: '主な原因',
      items: [
        { title: '1. サポート密度過高', desc: 'サポート密度が高すぎると、本体との接触面積が増え、除去が困難になります。' },
        { title: '2. 接触距離不足', desc: 'サポートと本体の距離（Z Distance）が小さすぎると、強く接着してしまいます。' },
        { title: '3. インターフェース設定不適切', desc: 'サポートインターフェース（屋根・床）の設定が不適切だと、除去が困難になります。' },
        { title: '4. 材料選択', desc: '本体と同じ材料でサポートを造形すると、接着が強くなり除去が困難になります。' }
      ]
    },
    solutions: {
      title: '対策・チェックポイント',
      items: [
        {
          title: 'サポート密度の調整',
          desc: '必要最小限の密度に設定します。',
          points: ['推奨密度: 10-15%', '大型造形でも20%以下', 'ツリーサポートなら5-10%でも十分']
        },
        {
          title: 'Z距離の調整',
          desc: 'サポートと本体の距離を適切に設定します。',
          points: ['Z Distance: 0.2-0.3mm（層高の1-1.5倍）', '除去しやすさ優先なら0.3mm以上', '仕上がり優先なら0.15-0.2mm']
        },
        {
          title: 'インターフェース設定の最適化',
          desc: 'サポートインターフェースで除去性を改善します。',
          points: ['インターフェース密度: 50-70%', 'インターフェース層数: 2-3層', 'インターフェースパターン: グリッドまたはライン']
        },
        {
          title: 'ツリーサポートの活用',
          desc: '通常サポートより除去が容易です。',
          points: ['接触面積が少なく除去しやすい', '材料の節約にもなる', '複雑な形状に適している']
        },
        {
          title: '水溶性サポート材の使用',
          desc: 'デュアルエクストルーダーがあれば最適な解決策です。',
          points: ['PVA: PLAと組み合わせ（水に溶ける）', 'HIPS: ABSと組み合わせ（リモネンに溶ける）', '完全に除去でき、跡が残らない']
        },
        {
          title: 'サポートブロッカーの活用',
          desc: '不要な部分のサポートを除外します。',
          points: ['手で届く部分はサポート不要', '見えない部分は仕上がりを妥協', 'スライサーで手動調整']
        }
      ]
    },
    removalTips: {
      title: 'サポート除去のコツ',
      items: [
        'ニッパーやペンチで大きな部分から除去',
        '細かい部分はカッターやデザインナイフで',
        'サンドペーパーで跡を研磨',
        '水溶性サポートは水に浸けて溶かす（6-24時間）',
        '無理に引っ張らず、少しずつ剥がす'
      ]
    },
    supportTypes: {
      title: 'サポートタイプ比較',
      items: [
        { title: '通常サポート', pros: '安定性が高い', cons: '除去が大変、材料消費多い' },
        { title: 'ツリーサポート', pros: '除去が容易、材料節約', cons: '大型造形では不安定な場合も' },
        { title: '水溶性サポート', pros: '完全除去、跡が残らない', cons: 'デュアルエクストルーダー必要、コスト高' },
        { title: 'サポートなし', pros: '除去不要、材料節約', cons: '45度以上のオーバーハングは困難' }
      ]
    },
    related: {
      title: '関連情報',
      items: [
        { title: '滲み・オーバーハング', desc: 'サポートが必要な状況', link: '/troubleshooting/bleeding' },
        { title: 'PLA フィラメント', desc: 'サポート除去が比較的容易', link: '/filament/pla' }
      ]
    },
    backLink: 'トラブル対策一覧に戻る'
  };

  const TEXT_EN = {
    breadcrumb: { home: 'Home', troubleshooting: 'Troubleshooting', title: 'Support Removal Difficulty' },
    badge: 'Minor',
    title: 'Support Removal Difficulty',
    description: 'Difficult support removal damaging the print. Can be improved with optimized settings.',
    symptoms: {
      title: 'Symptoms',
      items: [
        'Support strongly adhered to print, cannot remove',
        'Print surface damaged during support removal',
        'Support marks are noticeable',
        'Fine support pieces cannot be fully removed'
      ]
    },
    causes: {
      title: 'Main Causes',
      items: [
        { title: '1. Excessive Support Density', desc: 'Too high support density increases contact area with print, making removal difficult.' },
        { title: '2. Insufficient Contact Distance', desc: 'Too small Z Distance between support and print causes strong adhesion.' },
        { title: '3. Improper Interface Settings', desc: 'Incorrect support interface (roof/floor) settings make removal difficult.' },
        { title: '4. Material Selection', desc: 'Using same material for support as print increases adhesion, making removal difficult.' }
      ]
    },
    solutions: {
      title: 'Solutions & Checkpoints',
      items: [
        {
          title: 'Adjust Support Density',
          desc: 'Set to minimum necessary density.',
          points: ['Recommended density: 10-15%', '20% or less even for large prints', '5-10% sufficient for tree supports']
        },
        {
          title: 'Adjust Z Distance',
          desc: 'Set appropriate distance between support and print.',
          points: ['Z Distance: 0.2-0.3mm (1-1.5x layer height)', '0.3mm+ for easier removal', '0.15-0.2mm for better finish']
        },
        {
          title: 'Optimize Interface Settings',
          desc: 'Improve removability with support interface.',
          points: ['Interface density: 50-70%', 'Interface layers: 2-3 layers', 'Interface pattern: grid or lines']
        },
        {
          title: 'Use Tree Supports',
          desc: 'Easier to remove than normal supports.',
          points: ['Less contact area, easier removal', 'Saves material', 'Suitable for complex shapes']
        },
        {
          title: 'Use Soluble Support Material',
          desc: 'Optimal solution if dual extruder available.',
          points: ['PVA: with PLA (dissolves in water)', 'HIPS: with ABS (dissolves in limonene)', 'Complete removal, no marks']
        },
        {
          title: 'Use Support Blockers',
          desc: 'Exclude supports from unnecessary areas.',
          points: ['No support needed for accessible areas', 'Compromise finish for hidden areas', 'Manual adjustment in slicer']
        }
      ]
    },
    removalTips: {
      title: 'Support Removal Tips',
      items: [
        'Remove large sections first with nippers or pliers',
        'Use knife or design knife for fine areas',
        'Sand marks with sandpaper',
        'Dissolve soluble supports in water (6-24 hours)',
        'Don\'t pull forcefully, peel gradually'
      ]
    },
    supportTypes: {
      title: 'Support Type Comparison',
      items: [
        { title: 'Normal Support', pros: 'High stability', cons: 'Difficult removal, high material use' },
        { title: 'Tree Support', pros: 'Easy removal, material saving', cons: 'May be unstable for large prints' },
        { title: 'Soluble Support', pros: 'Complete removal, no marks', cons: 'Requires dual extruder, high cost' },
        { title: 'No Support', pros: 'No removal needed, material saving', cons: 'Difficult for overhangs above 45°' }
      ]
    },
    related: {
      title: 'Related Information',
      items: [
        { title: 'Sagging & Overhangs', desc: 'Situations requiring support', link: '/troubleshooting/bleeding' },
        { title: 'PLA Filament', desc: 'Relatively easy support removal', link: '/filament/pla' }
      ]
    },
    backLink: 'Back to Troubleshooting List'
  };

  const TEXT = isEnglish ? TEXT_EN : TEXT_JA;
  const prefix = isEnglish ? '/en' : '';

  const breadcrumbItems = [
    { label: TEXT.breadcrumb.home, path: `${prefix}/` },
    { label: TEXT.breadcrumb.troubleshooting, path: `${prefix}/troubleshooting` },
    { label: TEXT.breadcrumb.title }
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid" data-lang={isEnglish ? 'en' : 'ja'}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <span className="tech-label bg-[#5f8d63] text-white">{TEXT.badge}</span>
                <h1 className="text-4xl font-bold text-white font-['Exo']">
                  {TEXT.title}
                </h1>
              </div>
              <p className="text-metal-light text-lg">
                {TEXT.description}
              </p>
              <div className="section-divider"></div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.symptoms.title}
              </h2>
              <ul className="space-y-3 text-metal-light">
                {TEXT.symptoms.items.map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <i className="ri-arrow-right-s-line text-accent mt-1"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.causes.title}
              </h2>
              <div className="space-y-4">
                {TEXT.causes.items.map((item, idx) => (
                  <div key={idx} className="border-l-4 border-l-[#5f8d63] pl-4">
                    <h3 className="text-lg font-semibold text-accent font-['Exo'] mb-2">
                      {item.title}
                    </h3>
                    <p className="text-metal-light text-sm">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.solutions.title}
              </h2>
              <div className="space-y-4">
                {TEXT.solutions.items.map((item, idx) => (
                  <div key={idx} className="clickable-card p-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-[#5f8d63] text-xl mt-1"></i>
                      <div>
                        <h3 className="text-lg font-semibold text-white font-['Exo'] mb-2">
                          {item.title}
                        </h3>
                        <p className="text-metal-light text-sm mb-2">
                          {item.desc}
                        </p>
                        <ul className="text-metal-light text-sm space-y-1 ml-4">
                          {item.points.map((point, pidx) => (
                            <li key={pidx}>• {point}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.removalTips.title}
              </h2>
              <div className="space-y-3 text-metal-light">
                <ul className="space-y-2 ml-4">
                  {TEXT.removalTips.items.map((item, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <i className="ri-check-line text-[#5f8d63] mt-1"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.supportTypes.title}
              </h2>
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {TEXT.supportTypes.items.map((item, idx) => (
                    <div key={idx} className="border-l-4 border-l-accent pl-4">
                      <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                      <p className="text-metal-light text-sm mb-1">{isEnglish ? 'Pros' : 'メリット'}: {item.pros}</p>
                      <p className="text-metal-light text-sm">{isEnglish ? 'Cons' : 'デメリット'}: {item.cons}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.related.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {TEXT.related.items.map((item, idx) => (
                  <Link
                    key={idx}
                    to={`${prefix}${item.link}`}
                    className="clickable-card p-4 hover:border-l-4 hover:border-l-accent transition-all"
                  >
                    <h3 className="text-white font-['Exo'] font-semibold mb-1">
                      {item.title}
                    </h3>
                    <p className="text-metal-light text-sm">
                      {item.desc}
                    </p>
                  </Link>
                ))}
              </div>
            </div>

            <div className="flex justify-center">
              <Link
                to={`${prefix}/troubleshooting`}
                className="flex items-center space-x-2 text-accent hover:text-white transition-colors font-['Roboto_Mono']"
              >
                <i className="ri-arrow-left-line"></i>
                <span>{TEXT.backLink}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default SupportPage;
